<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">Cupones</h1>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start position-relative">
          <div class="w-full w-md-auto md-flex-1">
            <div class="d-flex justify-content-between align-items-center justify-content-lg-start">
              <button type="button" class="filter d-flex align-items-center justify-content-between border-0"
                @click="showFilters = !showFilters">
                <i class="ri-filter-3-line text-dark font-weight-bold"></i>
                <div>Filtrar</div>
              </button>
              <div class="d-md-none">
                <button type="button" class="button-tooltip text-dark font-weight-bold m-0" v-b-tooltip.hover
                  title="Recargar" @click="loadData()">
                  <i class="ri-loop-right-line"></i>
                </button>
              </div>
            </div>
          </div>

          <section class="w-full w-md-auto md-flex-1 d-flex justify-content-end align-items-center mt-2 mt-md-0">
            <div class="d-none d-md-flex">
              <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover title="Recargar"
                @click="loadData()">
                <i class="ri-loop-right-line"></i>
              </button>
            </div>
            <div class="w-full w-md-auto d-flex flex-column flex-md-row">
              <SearchBar placeholder="Buscar por código o descripción" @search="onSearch"
                @input="search = $event" class="flex-1" />
              <div class="text-right mt-2 mt-md-0">
                <b-button :to="{ name: 'config-coupon-create' }" variant="none"
                  class="button button-primary ml-md-2">Agregar</b-button>
              </div>
            </div>
          </section>

          <filter-menu :show="showFilters" @hide="showFilters = false" :options="filterOptions"
            :selectedOptionFilter="filters" @setFilters="setFilters"></filter-menu>
        </div>

        <div class="w-100 d-flex flex-wrap align-items-center mt-2" v-if="showFiltersList">
          <span v-for="(val, name) in filterList" v-bind:key="name" class="filter-item">
            {{ getFilterName(name, val) }}
            <button type="button" class="text-white p-0 bg-transparent border-0 ml-2" @click="deleteFilter(name)">
              <i class="ri-close-circle-line"></i>
            </button>
          </span>
        </div>
      </div>

      <div class="table-responsive mb-0 table-min-height">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th>Código</th>
              <th>Descripción</th>
              <th>Ciudad</th>
              <th>Tipo</th>
              <th>Valor</th>
              <th>Vigencia</th>
              <th>Usos</th>
              <th>Estado</th>
              <th class="pr-md-4">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="o in list" :key="o.id">
              <td class="font-weight-bold">{{ o.code }}</td>
              <td>{{ o.description || '—' }}</td>
              <td class="text-center">
                <span v-if="o.city_name">{{ o.city_name }}</span>
                <span v-else class="badge badge-secondary">Global</span>
              </td>
              <td class="text-center">
                <span :class="`badge ${couponTypeMeta(o.coupon_type).color}`">{{ couponTypeMeta(o.coupon_type).label }}</span>
              </td>
              <td class="text-center">{{ formatValue(o) }}</td>
              <td class="text-center">{{ formatVigencia(o) }}</td>
              <td class="text-center">{{ o.usage_count || 0 }}/{{ o.max_uses != null ? o.max_uses : '∞' }}</td>
              <td class="text-center">
                <span :class="`badge ${computeStatus(o).color}`">{{ computeStatus(o).label }}</span>
              </td>
              <td class="text-center d_option pr-md-3">
                <DropdownTable>
                  <template slot="dropdown-content">
                    <DropdownItem @click="$router.push({ name: 'config-coupon-edit', params: { id: o.id } })"
                      text="Editar" />
                    <DropdownItem :text="getStatus(o.is_active).action" @click="openChangeStatus(o)" />
                    <DropdownItem text="Eliminar" @click="openDelete(o)" />
                  </template>
                </DropdownTable>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <pagination :total="total" :page="page" :page-size="pageSize" :pages="pages"
          :changePage="changePage" :changePageSize="changePageSize" />
      </div>
    </div>

    <ConfirmationModal :show="confirmationModal.show" @hide="closeConfirmationModal"
      :data="confirmationModal" @confirm="confirmModal" />
  </div>
</template>

<script>
import { CouponService, GeographyService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";
import {
  Alert,
  Constants,
  PaginationMixin,
  DropdownTable,
  DropdownItem,
  SearchBar,
  FilterMenu,
  FilterMixin,
  ConfirmationModal,
  ConfirmationModalMixin
} from "wize-admin";

export default {
  components: { SearchBar, FilterMenu, DropdownTable, DropdownItem, ConfirmationModal },
  mixins: [PaginationMixin, FilterMixin, ConfirmationModalMixin],
  data() {
    return {
      list: [],
      cities: [],
    };
  },
  computed: {
    // Filtros del panel. El de ciudad se arma con las ciudades activas + "Global".
    filterOptions() {
      return [
        {
          label: "Estado",
          key: "is_active",
          type: Constants.TypeFilters.SINGLE,
          options: [
            { key: true, label: "Activo" },
            { key: false, label: "Suspendido" },
          ],
        },
        {
          label: "Tipo",
          key: "coupon_type",
          type: Constants.TypeFilters.SINGLE,
          options: [
            { key: "PERCENTAGE", label: "Porcentaje" },
            { key: "FIXED_AMOUNT", label: "Monto fijo" },
            { key: "FIXED_PRICE", label: "Precio fijo" },
            { key: "PRICE_LIST", label: "Lista de precios" },
          ],
        },
        {
          label: "Ciudad",
          key: "city",
          type: Constants.TypeFilters.SINGLE,
          options: [
            { key: "global", label: "Global" },
            ...this.cities.map((c) => ({ key: c.id, label: c.name })),
          ],
        },
      ];
    },
  },
  methods: {
    async loadCities() {
      try {
        const res = await GeographyService.listCities();
        this.cities = (res.payload || []).filter((c) => c.is_active);
      } catch (e) {
        console.error(e);
      }
    },
    getStatus(isActive) {
      return isActive ? LocalConstants.CouponStatus.ACTIVE : LocalConstants.CouponStatus.INACTIVE;
    },
    // Etiqueta + color del badge según el tipo de cupón.
    couponTypeMeta(type) {
      const map = {
        PERCENTAGE: { label: 'Porcentaje', color: 'badge-info' },
        FIXED_AMOUNT: { label: 'Monto fijo', color: 'badge-primary' },
        FIXED_PRICE: { label: 'Precio fijo', color: 'badge-teal' },
        PRICE_LIST: { label: 'Lista de precios', color: 'badge-secondary' },
      };
      return map[type] || { label: type || '—', color: 'badge-secondary' };
    },
    formatValue(o) {
      switch (o.coupon_type) {
        case 'PERCENTAGE': return `${o.discount_value}%`;
        case 'PRICE_LIST': return o.price_type_name || 'Lista de precios';
        default: return `S/ ${parseFloat(o.discount_value).toFixed(2)}`; // FIXED_AMOUNT / FIXED_PRICE
      }
    },
    // Estado calculado: Inactivo > Expirado > Agotado > Activo.
    computeStatus(o) {
      if (!o.is_active) return { label: 'Inactivo', color: 'badge-yellow' };
      if (o.end_date) {
        const end = new Date(o.end_date.substring(0, 10));
        const today = new Date(new Date().toISOString().slice(0, 10));
        if (today > end) return { label: 'Expirado', color: 'badge-dark' };
      }
      if (o.max_uses != null && (o.usage_count || 0) >= o.max_uses) {
        return { label: 'Agotado', color: 'badge-danger' };
      }
      return { label: 'Activo', color: 'badge-green' };
    },
    formatVigencia(o) {
      if (!o.start_date && !o.end_date) return '—';
      const fmt = (d) => {
        if (!d) return '...'
        const [y, m, day] = d.substring(0, 10).split('-')
        return new Date(y, m - 1, day).toLocaleDateString('es-PE', {
          day: '2-digit', month: 'short', year: 'numeric'
        })
      }
      return `${fmt(o.start_date)} — ${fmt(o.end_date)}`;
    },
    openChangeStatus(o) {
      this.selectedOption = o;
      this.openConfirmationModal({
        title: "Cambiar estado",
        description: "¿Está seguro que desea cambiar el estado del cupón?",
        acceptText: "Aceptar",
        action: "update",
      });
    },
    openDelete(o) {
      this.selectedOption = o;
      this.openConfirmationModal({
        title: "Eliminar cupón",
        description: `¿Está seguro que desea eliminar el cupón '${o.code}'? Esta acción no se puede deshacer.`,
        acceptText: "Eliminar",
        action: "delete",
      });
    },
    confirmModal() {
      this.closeConfirmationModal();
      if (this.confirmationModal.action === "update") this.changeStatus();
      if (this.confirmationModal.action === "delete") this.deleteCoupon();
    },
    async changeStatus() {
      try {
        await this.$store.dispatch("app/loading", true);
        const newActive = !this.selectedOption.is_active;
        await CouponService.changeStatus(this.selectedOption.id, newActive);
        await this.loadData();
        Alert.success("Estado actualizado correctamente");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors?.message || "Error al actualizar el estado");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async deleteCoupon() {
      try {
        await this.$store.dispatch("app/loading", true);
        await CouponService.remove(this.selectedOption.id);
        await this.loadData();
        Alert.success("Cupón eliminado correctamente");
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors?.message || "Error al eliminar el cupón");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async loadData() {
      try {
        await this.$store.dispatch("app/loading", true);
        const params = { page: this.page - 1, size: this.pageSize };
        const data = { search: this.search, ...this.filters };
        // Traducir el filtro de ciudad al contrato del backend:
        // "global" → only_global; un id → city_id; sin selección → todos.
        if (data.city !== undefined && data.city !== null) {
          if (data.city === "global") data.only_global = true;
          else data.city_id = Number(data.city);
        }
        delete data.city;
        const response = await CouponService.list(data, params);
        this.list = JSON.parse(JSON.stringify(response.payload.items || []));
        this.total = response.payload?.metadata.totalElements;
        this.pages = response.payload?.metadata.totalPages;
      } catch (error) {
        console.error(error);
        Alert.error(error);
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
  },
  async mounted() {
    await this.loadCities();
    await this.loadData();
  },
};
</script>

<style lang="stylus" scoped>
.badge-teal
  background-color #20c997
  color #fff
</style>
